import './App.css'
import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Route, Routes, Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { trackPixelPageView } from './services/metaPixel'

// Shell statique (layout) — toujours nécessaire.
// NOTE : Launcher (splash 2 s) et InitialProfile (page pseudo) ne sont plus
// routés. La plateforme est en accès libre : on arrive directement sur
// l'accueil, et l'identité n'est demandée qu'au moment de commander. Les
// fichiers sont conservés mais plus aucune route n'y mène.
import Layout    from './Components/Layout/Layout'
import CookieConsent from './Components/CookieConsent/CookieConsent'
import { useAuth } from './Context/AuthContext'

// Pages chargées À LA DEMANDE (code-splitting). Le bundle initial passe de
// ~1 Mo à un noyau léger ; chaque page n'est téléchargée qu'à sa 1re visite.
const Home               = lazy(() => import('./Pages/Home/Home'))
const ProductDesc        = lazy(() => import('./Pages/ProductDesc/ProductDesc'))
const GetStarted         = lazy(() => import('./Pages/Launcher/GetStarted/GetStarted'))
const PhoneAuth          = lazy(() => import('./Pages/Auth/PhoneAuth/PhoneAuth'))
const Profile            = lazy(() => import('./Pages/Profile/Profile'))
const ValidationPurchase = lazy(() => import('./Pages/ValidationPurchase/ValidationPurchase'))
const Account            = lazy(() => import('./Pages/Profile/Account/Account'))
const Basket             = lazy(() => import('./Pages/Profile/Basket/Basket'))
const Orders             = lazy(() => import('./Pages/Profile/Orders/Orders'))
const OrderDetail        = lazy(() => import('./Pages/Profile/Orders/OrderDetail'))
const About              = lazy(() => import('./Pages/Profile/About/About'))
const CategoryPage       = lazy(() => import('./features/catalog/pages/CategoryPage'))
const ImportService      = lazy(() => import('./Pages/Import/ImportService'))
const MyImports          = lazy(() => import('./Pages/Import/MyImports'))
const ImportDetail       = lazy(() => import('./Pages/Import/ImportDetail'))
const Favorites          = lazy(() => import('./Pages/Profile/Favorites/Favorites'))
const Addresses          = lazy(() => import('./Pages/Profile/Addresses/Addresses'))
const ChangePhone        = lazy(() => import('./Pages/Profile/ChangePhone/ChangePhone'))
const Notifications      = lazy(() => import('./Pages/Notifications/Notifications'))
const Support            = lazy(() => import('./Pages/Profile/Support/Support'))
const NewClaim           = lazy(() => import('./Pages/Profile/Support/NewClaim'))
const ClaimDetail        = lazy(() => import('./Pages/Profile/Support/ClaimDetail'))

/* ─────────────────────────────────────────────
   Spinner minimal pendant résolution auth
───────────────────────────────────────────── */
const Splash = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', background: '#000',
  }}>
    <span style={{ color: '#c9a84c', fontSize: 32 }}>●</span>
  </div>
);

/* ─────────────────────────────────────────────
   Route protégée — auth + profil complet requis.
   Réservée à ce qui touche à une commande réelle
   (paiement, suivi, adresses). Le profil se
   complète dans /Profile/Account : il n'y a plus
   de page dédiée /Setup.
───────────────────────────────────────────── */
const ProtectedRoute = () => {
  const { user, profileLevel, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Splash />;
  if (!user)   return <Navigate to="/Connexion" replace state={{ from: location }} />;

  if (profileLevel !== 'complete') {
    return <Navigate to="/Profile/Account" replace state={{ from: location }} />;
  }

  return <Outlet />;
};

/* ─────────────────────────────────────────────
   Route auth simple — connecté (OTP) suffit,
   profil complet NON requis. Le profil est
   complété directement dans le formulaire
   Ornoir Express (tunnel demande d'import).
───────────────────────────────────────────── */
const RequireAuth = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Splash />;
  if (!user)   return <Navigate to="/Connexion" replace state={{ from: location }} />;

  return <Outlet />;
};

/* ─────────────────────────────────────────────
   Route de connexion — inaccessible si déjà connecté.
   Renvoie sur la page d'origine (`state.from`, posée
   par les gardes ou par le panier) : c'est ce qui
   ramène l'utilisateur à son panier après l'OTP.
   Sans origine connue → accueil.
───────────────────────────────────────────── */
const PublicRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Splash />;
  if (!user)   return <Outlet />;

  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const target = from?.pathname ? `${from.pathname}${from.search ?? ''}` : '/Home';
  return <Navigate to={target} replace />;
};

/* ─────────────────────────────────────────────
   ShareRedirect — lien court de partage
   /s/:productId → /Product?id=:productId
   La fiche produit est en accès libre : le lien
   partagé ouvre directement le produit, sans
   aucune étape de connexion.
───────────────────────────────────────────── */
const ShareRedirect = () => {
  const { productId } = useParams();
  const [params] = useSearchParams();
  if (!productId) return <Navigate to="/Home" replace />;
  const ref = params.get('ref');
  const target = `/Product?id=${encodeURIComponent(productId)}`
    + (ref ? `&ref=${encodeURIComponent(ref)}` : '');
  return <Navigate to={target} replace />;
};

/* ─────────────────────────────────────────────
   RouteTracker — à chaque changement de route :
   • PageView Pixel Meta (sinon seule la 1re vue compte)
   • retour en haut de page (sinon on arrive au milieu,
     ex. clic produit en bas de liste → fiche scrollée).
───────────────────────────────────────────── */
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPixelPageView();
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);
  return null;
};

/* ─────────────────────────────────────────────
   App
───────────────────────────────────────────── */
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <RouteTracker />
        <Suspense fallback={<Splash />}>
        <Routes>
          {/* ═══════════════════════════════════════════════════════
              ACCÈS LIBRE — aucune vérification.
              On ouvre le lien (ou l'app installée) et on tombe
              directement sur l'accueil : catalogue, fiches produit,
              panier et favoris se manipulent sans compte, comme sur
              Amazon ou Alibaba. Le code de vérification (par e-mail)
              n'arrive qu'au paiement.
              ═══════════════════════════════════════════════════════ */}
          <Route element={<Layout />}>
            <Route path="/"        element={<Home />} />
            <Route path="/Home"    element={<Home />} />
            <Route path="/Product" element={<ProductDesc />} />
          </Route>

          <Route path="/category/:id" element={<CategoryPage />} />

          {/* Favoris : store local tant qu'on n'a pas de compte, remontés
              dans Firestore à la première connexion. */}
          <Route path="/Favorites" element={<Favorites />} />

          {/* Lien court de partage (réseaux sociaux) */}
          <Route path="/s/:productId" element={<ShareRedirect />} />

          {/* Onboarding — plus dans le parcours d'entrée, mais l'URL reste
              valide (campagnes, liens existants). */}
          <Route path="/GetStarted" element={<GetStarted />} />
          <Route path="/Launcher"   element={<Navigate to="/" replace />} />
          {/* Ancienne page pseudo : l'identité se saisit désormais dans
              /Profile/Account, au moment de commander. */}
          <Route path="/Setup"      element={<Navigate to="/Profile/Account" replace />} />

          {/* ── CONNEXION (inaccessible si déjà connecté) ── */}
          <Route element={<PublicRoute />}>
            <Route path="/Connexion" element={<PhoneAuth />} />
          </Route>

          {/* ═══════════════════════════════════════════════════════
              ESPACE PROFIL — coquille commune (DrawBar + BottomBar).
              Le panier y est public : il se remplit sans compte, et
              c'est le bouton « Passer au paiement » qui déclenche
              l'OTP (voir Basket.tsx).
              ═══════════════════════════════════════════════════════ */}
          <Route path="/Profile" element={<Profile />}>
            <Route index            element={<Navigate to="Basket" replace />} />
            <Route path="Basket"    element={<Basket />} />
            <Route path="Favorites" element={<Favorites />} />
            <Route path="About"     element={<About />} />

            {/* Connecté requis — c'est ici qu'on complète nom + adresse. */}
            <Route element={<RequireAuth />}>
              <Route path="Account" element={<Account />} />

              {/* Réclamations : l'OTP suffit. On ne demande pas au client de
                  compléter son profil avant de pouvoir se plaindre — la
                  réclamation peut justement porter sur son compte. */}
              <Route path="Support"           element={<Support />} />
              <Route path="Support/Nouvelle"  element={<NewClaim />} />
              <Route path="Support/:claimId"  element={<ClaimDetail />} />
            </Route>

            {/* Connecté + profil complet */}
            <Route element={<ProtectedRoute />}>
              <Route path="Orders" element={<Orders />} />
            </Route>
          </Route>

          {/* ── CONNECTÉ (OTP suffit) ── */}
          <Route element={<RequireAuth />}>
            <Route element={<Layout />}>
              <Route path="/Import"            element={<ImportService />} />
              <Route path="/Import/Requests"   element={<MyImports />} />
              <Route path="/Import/:requestId" element={<ImportDetail />} />
            </Route>
            <Route path="/Notifications" element={<Notifications />} />
          </Route>

          {/* ── CONNECTÉ + PROFIL COMPLET — tout ce qui touche à une
                 commande réelle (paiement, suivi, adresses) ── */}
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route path="/Validate/:orderId" element={<ValidationPurchase />} />
            </Route>
            <Route path="/Profile/Orders/:orderId" element={<OrderDetail />} />
            <Route path="/ChangePhone" element={<ChangePhone />} />
            <Route path="/Addresses"   element={<Addresses />} />
          </Route>

          {/* ── FALLBACK ── */}
          <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
        </Suspense>
      </BrowserRouter>
      <CookieConsent />
    </div>
  );
}

export default App;
